import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoanCard, LoanDetail, LoanStatements, LoanStatement, LoanGeneralStatusCode } from '../models/loan.model';
import { LoanStatementDto } from '../models/dtos/loan-statement.dto';
import { PaymentPeriod, PaymentType } from '../models/payment.model';
import { AuthService } from './auth/auth.service';
import { LoanStatementsMapper } from '../mappers/loan-statements.mapper';
import { getLoansUrl, getLoanDetailsUrl, getStatementsUrl } from '../shared/utils/api-urls.utils';

/**
 * Service to handle all loan-related API calls
 */
@Injectable({
  providedIn: 'root'
})
export class LoanService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  /**
   * Gets all loans for the current user
   * @returns Observable of loan cards
   */
getLoans(): Observable<LoanCard[]> {
    const token = this.auth.token;

  const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

  return this.http
      .get<any[]>(getLoansUrl(), { headers })
    .pipe(
      map(rawLoans =>
        rawLoans.map(l => ({
          // LoanCard interface says:
          // readonly id: number;
          id: Number(l.acctrefno),

          // readonly number: string;
          number: l.loan_number,

          // readonly nickname: string;
nickname: (l.shortname ?? '').replace(/^108-/, ''),

          // readonly payoffBalance: number;
          payoffBalance: Number(l.current_payoff_balance),

          // readonly pastDue: number;
          pastDue: Number(l.total_past_due_balance),

          // readonly pastDueDate: Date | null;
          // assuming if pastDue > 0 you might look at last_billing_date,
          // otherwise null
          pastDueDate: l.total_past_due_balance > 0
            ? new Date(l.last_billing_date)
            : null,

          // readonly nextPaymentDueAmount: number;
          // using the next payoff balance as the “due amount”
          nextPaymentDueAmount: Number(l.total_current_due_balance),

          // readonly nextPaymentDueDate: Date;
          // pick next_recurring_trans_date if present, else next_billing_date
          nextPaymentDueDate: new Date(
            l.next_recurring_trans_date ?? l.next_billing_date
          ),

          // readonly setupDate: Date;
          // use the loan “open date” as its setup
          setupDate: new Date(l.open_date),

          // readonly statusCode: LoanGeneralStatusCode;
          // assume your enum lines up with status_code_no
          statusCode: l.status_code_no,

          // readonly isSelected: boolean;
          // you’ll stamp this later in your component via route params
          isSelected: false,

          // optional portfolio_code_id for special handling
          portfolio_code_id: l.portfolio_code_id
        }))
      )
    );
}

  /**S
   * Fetches loan details by ID.
   * @param id The ID of the loan.
   * @returns An observable of the loan details or null if not found or an error occurs.
   */
getLoanDetails(id: string): Observable<LoanDetail | null> {
  if (!id) {
    return of(null);
  }
  const token = this.auth.token;

  const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

  return this.http
    .get<any>(getLoanDetailsUrl(id), { headers })
    .pipe(
      map(l => {
        if (!l) {
          return null;
        }

        const detail: LoanDetail = {
          // basic identifiers
          statusCode: Number(l.status_code_no) as LoanGeneralStatusCode,
          number:                     l.loan_number,
nickname: (l.shortname ?? '').replace(/^108-/, ''),
          openDate:                   new Date(l.open_date),
          maturityDate:               l.curr_maturity_date
                                        ? new Date(l.curr_maturity_date)
                                        : null,
          loanAmount:                 Number(l.original_note_amount),

          // balances
          principal:                  Number(l.current_principal_balance),
          interest:                   Number(l.current_interest_balance),
          fees:                       Number(l.current_fees_balance),
          lateCharges:                Number(l.current_late_charge_balance),
          otherCharges:               Number(l.current_udf1_balance) + Number(l.current_udf2_balance),
          totalBalance:               Number(l.current_payoff_balance),

          // impounds / limits
          currentImpound:             Number(l.current_suspense),
          creditLineLimit:            Number(l.current_udf2_balance),

          // last payment (if your API returns these; otherwise null)
          lastPayment:                l.last_payment_amount
                                        ? Number(l.last_payment_amount)
                                        : null,
          lastPaymentReceivedDate:    l.last_payment_date
                                        ? new Date(l.last_payment_date)
                                        : null,

          // rates & YTD
          currentRate:                Number(l.current_interest_rate), // to percent
          interestPaidYTD:            l.interest_paid_ytd
                                        ? Number(l.interest_paid_ytd)
                                        : 0,

          // payoff info
          payoffAmount:               Number(l.current_payoff_balance),
          payoffDate:                 l.next_statement1_date
                                        ? new Date(l.next_statement1_date)
                                        : new Date(l.next_billing_date),

          // next payment (you can also map a recurring-trans date if API provides)
          nextPaymentDueAmount:       Number(l.total_current_due_balance),
          nextPaymentDueDate:         new Date(l.next_recurring_trans_date ?? l.next_billing_date),
          portfolio_code_id:          l.portfolio_code_id,

          // payment settings & recurring
          paymentSettings: {
            recurringPaymentExists: Boolean(
              l.paymentSettings?.recurringPaymentExists
            ),
            existingRecurringPaymentDisabled: Boolean(l.recurring_disabled),
            disableCardPayment:            Boolean(l.disable_card_payment),
            disableBankingPayment:         Boolean(l.disable_bank_payment),
            disableCardRecurringPayment:   Boolean(l.disable_card_recurring),
            disableBankingRecurringPayment: Boolean(l.disable_bank_recurring),
          },
recurringPayment: l.recurringPayment
  ? {
      // map your PK
      id: Number(l.recurringPayment.row_id),

      // consider “status” 0 = active, anything else = disabled
      isActive: l.recurringPayment.status === 0,

      // if you only support fixed‐amount autopay, take amount_fixed; otherwise null
      paymentAmount: l.recurringPayment.amount_fixed
        ? Number(l.recurringPayment.amount_fixed)
        : null,

      // billing_type: 0 = billing (one‐off), 1 = recurring
      billingType:
        l.recurringPayment.billing_type === 0 ? 'billing' : 'recurring',

      // start / end dates
      startDate: new Date(l.recurringPayment.billing_start_date),
      endDate: l.recurringPayment.billing_expire_date
        ? new Date(l.recurringPayment.billing_expire_date)
        : null,

      // frequency code maps straight onto your enum
      frequency: l.recurringPayment.billing_period as PaymentPeriod,

      // ACH vs card: pendingPayments only does bank, but if you later add cards:
      paymentMethod: PaymentType.Bank,

      // last four digits: strip the masks  
      lastFourDigits: (() => {
        const acct = l.recurringPayment.account_number;
        // your service returns an encrypted blob, so if you can’t decrypt,
        // you may already be storing lastFour in a separate column.
        // Fallback: return undefined
        return acct && acct.length >= 4
          ? acct.slice(-4)
          : undefined;
      })(),

      // full routing & account numbers if you want them (you may also choose
      // to omit the encrypted payload)
      routingNumber: l.recurringPayment.aba_number,
      accountNumber: undefined
    }
  : null        };

        return detail;
      }),
      catchError(() => of(null))
    );
}


  getLoanStatements(id: string): Observable<LoanStatements> {
    if (!id) {
      throw new Error('Loan ID is required');
    }
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.auth.token}`
    });

    return this.http
      .get<LoanStatementDto[]>(getStatementsUrl(id), { headers })
      .pipe(
        map(stmts => LoanStatementsMapper.toModel(stmts)),
        catchError(() => {
          throw new Error(GET_LOAN_STATEMENTS_ERROR);
        })
      );
  }
}

export const GET_LOAN_STATEMENTS_ERROR = 'Failed to fetch loan statements';
